import { z } from "zod";
import { CarryInBaseSchema } from "../generated/CarryIn";
import { MaterialBaseSchema } from "../generated/Material";
import { LotBaseSchema } from "../generated/Lot";
import { ConstructionBaseSchema } from "../generated/Construction";
import { CarryOutBaseSchema } from "../generated/CarryOut";
import { ConstructionPlanBaseSchema } from "../generated/ConstructionPlan";
import { ConstructionRecordBaseSchema } from "../generated/ConstructionRecord";
import { ConstructionPlanAreaBaseSchema } from "../generated/ConstructionPlanArea";
import { IssuanceBaseSchema } from "../generated/Issuance";
import { ProcessExecutionBaseSchema } from "../generated/ProcessExecution";
import { ConstructionMaterialConsumptionBaseSchema } from "../generated/ConstructionMaterialConsumption";
import { GroupBaseSchema } from "../generated/Group";
import { WorkBaseSchema } from "../generated/Work";
import { getUserSchema } from "./user";

// 材料QR番号の閉区間(start ≤ end)。id を全展開せず範囲のまま送ることでペイロード肥大を防ぐ。
export const idRangeSchema = z.object({
  start: z.number().int().min(1),
  end: z.number().int().min(1),
});
// 注意: 共通の parseStrip が zod の refine を正しく再適用できない(常に失敗する)ため、
// start ≤ end はスキーマでは強制しない(end < start の範囲は BETWEEN が空集合になるだけで無害)。
export const getMaterialFromIdSearchPayloadSchema = z.object({
  targetRanges: idRangeSchema.array().max(500),
});
export type getMaterialFromIdSearchPayload = z.infer<typeof getMaterialFromIdSearchPayloadSchema>;

// マテリアル検索時、マテリアルが持つデータ
export const IssuanceForMaterialSearchSchema = IssuanceBaseSchema.base.extend({
  User: getUserSchema.strip()
}).nullable();
export const LotForMaterialSearchSchema = LotBaseSchema.base.extend({
  CarryOut: CarryOutBaseSchema.base.extend({
    User: getUserSchema.strip()
  }).nullable(),
  User: getUserSchema.strip()
}).nullable();
export const CarryInForMaterialSearchSchema = CarryInBaseSchema.base.extend({
  // 工事は Work(work_id で解決)。Work は担当者を持たないため User ネストは無い。
  Work: WorkBaseSchema.base.nullable(),
  User: getUserSchema.strip()
}).nullable();
export const ConstructionRecordForMaterialSearchSchema = ConstructionRecordBaseSchema.base.extend({
  User: getUserSchema.strip()
}).array().nullable();
export const ProcessExecutionForMaterialSearchSchema = ProcessExecutionBaseSchema.base.extend({
  ConstructionPlanArea: ConstructionPlanAreaBaseSchema.base.nullable().optional()
}).array().nullable().optional();
export const ConstructionForMaterialSearchSchema = ConstructionBaseSchema.base.extend({
  ConstructionPlan: ConstructionPlanBaseSchema.base.extend({
    // 工事は Work(施工計画の work_id で解決)。Work は担当者を持たないため User ネストは無い。
    Work: WorkBaseSchema.base.nullable(),
    User: getUserSchema.strip()
  }).nullable(),
  ConstructionRecords: ConstructionRecordForMaterialSearchSchema,
  ProcessExecutions: ProcessExecutionForMaterialSearchSchema,
  User: getUserSchema.strip()
}).nullable();

export type IssuanceForMaterialSearch = z.infer<typeof IssuanceForMaterialSearchSchema>;
export type LotForMaterialSearch = z.infer<typeof LotForMaterialSearchSchema>;
export type CarryInForMaterialSearch = z.infer<typeof CarryInForMaterialSearchSchema>;
export type ConstructionForMaterialSearch = z.infer<typeof ConstructionForMaterialSearchSchema>;
export type ConstructionRecordForMaterialSearch = z.infer<typeof ConstructionRecordForMaterialSearchSchema>;

// QRごとの消費明細(部分消費対応)。alive のみ・createdAt 昇順でサーバが付与する。
// Construction は参照先施工の nest(履歴Projection)。削除済み施工も deleted_at 付きで含まれ、
// 「施工が削除済み」を行単位で判別できる。施工行が見つからない場合は null。
// ProcessExecutions は消費先施工が複数のとき、ライフサイクルで工程・施工箇所を全施工横断で表示するための nest
// (オフラインの IndexedDB フォールバック応答には含まれないことがあるため optional)。
export const MaterialConsumptionSchema = z.object({
  construction_id: z.string().uuid(),
  used_ratio: z.number(),
  createdAt: z.number(),
  deleted_at: z.number().nullable(),
  Construction: z
    .object({
      id: z.string().uuid(),
      deleted_at: z.number().nullable(),
      ProcessExecutions: ProcessExecutionForMaterialSearchSchema,
      User: getUserSchema.strip().nullable().optional(),
    })
    .nullable(),
});
export type MaterialConsumption = z.infer<typeof MaterialConsumptionSchema>;

export const SearchedmaterialSchema = MaterialBaseSchema.base.extend({
  Issuance: IssuanceForMaterialSearchSchema,
  Lot: LotForMaterialSearchSchema,
  CarryIn: CarryInForMaterialSearchSchema,
  Construction: ConstructionForMaterialSearchSchema,
  Consumptions: MaterialConsumptionSchema.array(),
});

export const searchedMaterialRowsSchema = SearchedmaterialSchema.array();

// 材料検索の正規化応答(重複排除)。全関連エンティティを id 重複なしの平坦なバケットで受け、
// ネスト形(searchedMaterial)への復元は util/hydrators/materialSearchHydrator の共通ハイドレータが行う。
// キー名は IndexedDB の SetCache 収集キー(PascalCase)に合わせてあり、応答をそのままキャッシュできる。
export const normalizedMaterialSearchResponseSchema = z.object({
  Materials: MaterialBaseSchema.base.array(),
  // 材料ごとの代表施工(alive 消費が指す最新の生存施工。無ければ削除済み施工へフォールバック)。
  // 代表の選定はサーバ側の従来ロジックが行い、クライアントは再導出しない。
  Representatives: z.object({ material_id: z.number(), construction_id: z.string().uuid().nullable() }).array(),
  related: z.object({
    Issuances: IssuanceBaseSchema.base.array(),
    Lots: LotBaseSchema.base.array(),
    CarryOuts: CarryOutBaseSchema.base.array(),
    CarryIns: CarryInBaseSchema.base.array(),
    Works: WorkBaseSchema.base.array(),
    Constructions: ConstructionBaseSchema.base.array(),
    ConstructionPlans: ConstructionPlanBaseSchema.base.array(),
    ConstructionRecords: ConstructionRecordBaseSchema.base.array(),
    ProcessExecutions: ProcessExecutionBaseSchema.base.array(),
    ConstructionPlanAreas: ConstructionPlanAreaBaseSchema.base.array(),
    ConstructionMaterialConsumptions: ConstructionMaterialConsumptionBaseSchema.base.array(),
    // 汎用ノーマライザは User 内の Group ネストも一般規則どおり分離する(Users は group_id 参照のみ)
    Users: getUserSchema.omit({ Group: true }).strip().array(),
    Groups: GroupBaseSchema.base.array(),
  }),
});
export type NormalizedMaterialSearchResponse = z.infer<typeof normalizedMaterialSearchResponseSchema>;
export type searchedMaterial = z.infer<typeof SearchedmaterialSchema>;
export type searchedMaterialRows = z.infer<typeof searchedMaterialRowsSchema>;

